import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { logo } from '../assets/images';

const DEALER_PORTAL_URL = 'https://concessionnaire.financesunshine.com/login';

type NavItem = {
  name: string;
  path: string;
  dropdownItems?: { name: string; path: string }[];
  external?: boolean;
};

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  const services = [
    { name: t('services.carFinancing'), path: '/financement/particulier' },
    { name: t('services.alternativeCredit'), path: '/financement/credit-alternatif' },
    { name: t('services.commercialFinancing'), path: '/financement/commercial' },
    { name: t('services.dealerServices'), path: '/concessionnaires' },
    { name: t('services.creditRepair'), path: '/financement/redressement-credit' },
    { name: t('services.flexibleSolutions'), path: '/financement/solutions-flexibles' }
  ];

  const navItems: NavItem[] = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '#', dropdownItems: services },
    { name: t('nav.contact'), path: '/contact' },
    { name: t('nav.dealerPortal'), path: DEALER_PORTAL_URL, external: true }
  ];

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isServicesOpen && !(event.target as Element).closest('.services-dropdown')) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isServicesOpen]);

  // Close dropdown when route changes
  useEffect(() => {
    setIsServicesOpen(false);
  }, [location.pathname]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="Sunshine Finance inc."
              className="h-12 w-auto"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8">
          {navItems.map((item) => (
            <div key={item.name} className="relative">
              {item.dropdownItems ? ( 
                <div className="services-dropdown">
                  <button
                    onClick={toggleServices}
                    className={`text-sm font-medium transition-colors hover:text-secondary-600 flex items-center ${
                      location.pathname.startsWith('/financement') || location.pathname === '/concessionnaires'
                        ? 'text-secondary-600'
                        : isScrolled 
                          ? 'text-primary-900' 
                          : 'text-primary-700'
                    }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown size={16} className={`ml-1 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isServicesOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-100">
                      {item.dropdownItems.map((service) => (
                        <NavLink
                          key={service.name}
                          to={service.path}
                          className={({ isActive }) => `
                            block px-4 py-2 text-sm transition-colors
                            ${isActive 
                              ? 'bg-secondary-50 text-secondary-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                            }
                          `}
                          onClick={() => setIsServicesOpen(false)}
                        >
                          {service.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.external ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium transition-colors hover:text-secondary-600 ${
                    isScrolled ? 'text-primary-900' : 'text-primary-700'
                  }`}
                >
                  {item.name}
                </a>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-secondary-600 ${
                      isActive
                        ? 'text-secondary-600 border-b-2 border-secondary-600 pb-1'
                        : isScrolled
                          ? 'text-primary-900'
                          : 'text-primary-700'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              )}
            </div>
          ))}
        </nav>

        <button
          className="hidden md:flex items-center px-6 py-2.5 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          onClick={() => window.location.href = '/contact'}
        >
          {t('nav.getStarted')}
        </button>
        
        <div className="hidden md:block ml-6">
          <LanguageSwitcher />
        </div>

        {/* Mobile Navigation Toggle */}
        <button 
          className="md:hidden text-primary-900 focus:outline-none"
          onClick={handleMenuToggle}
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-md animate-fade-in">
          <div className="container mx-auto px-6 py-4 flex flex-col space-y-4">
            {navItems.map((item) => {
              if (item.dropdownItems) {
                return (
                  <div key={item.name} className="space-y-2">
                    <div className="font-medium text-sm text-gray-900">{item.name}</div>
                    <div className="pl-4 space-y-2">
                      {item.dropdownItems.map((service) => (
                        <Link
                          key={service.name}
                          to={service.path}
                          className="block text-sm text-gray-700 hover:text-secondary-600"
                          onClick={closeMenu}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              if (item.external) {
                return (
                  <a
                    key={item.name}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 text-sm font-medium text-primary-900 transition-colors hover:text-secondary-600"
                    onClick={closeMenu}
                  >
                    {item.name}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `py-2 text-sm font-medium transition-colors hover:text-secondary-600 ${
                      isActive ? 'text-secondary-600 bg-gray-50 px-4 rounded-md' : 'text-primary-900'
                    }`
                  }
                  onClick={closeMenu}
                >
                  {item.name}
                </NavLink>
              );
            })}
            <button
              className="w-full py-2.5 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              onClick={() => {
                closeMenu();
                window.location.href = '/contact';
              }}
            >
              Nous joindre
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
